import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const AdminBusinessSourcesModal = ({ 
  open, 
  onClose, 
  businessId, 
  businessName, 
  onSuccess,
  API_URL 
}) => {
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState({});
  const [activeSources, setActiveSources] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form states
  const [newJustEatUrl, setNewJustEatUrl] = useState('');
  const [newDeliverooUrl, setNewDeliverooUrl] = useState('');
  const [newUberEatsUrl, setNewUberEatsUrl] = useState('');
  const [newTripadvisorUrl, setNewTripadvisorUrl] = useState('');
  const [newBookingUrl, setNewBookingUrl] = useState('');

  const [newYelpUrl, setNewYelpUrl] = useState('');
  const [newDesignMyNightUrl, setNewDesignMyNightUrl] = useState('');
  const [newOpenTableUrl, setNewOpenTableUrl] = useState('');
  const [newTrustPilotUrl, setNewTrustPilotUrl] = useState('');
  const [addingSource, setAddingSource] = useState(false);
  
  // Fetch reviews states
  const [sizeLimit, setSizeLimit] = useState(5000);
  const [fetchingReviews, setFetchingReviews] = useState(false);
  const [fetchResults, setFetchResults] = useState(null);

  useEffect(() => {
    if (open && businessId) {
      fetchBusinessSources();
    }
  }, [open, businessId]);

  const fetchBusinessSources = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/sources`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch business sources');
      }
      
      const data = await response.json();
      setSources(data.source_identifiers || {});
      setActiveSources(data.active_sources || []);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddJustEatUrl = async () => {
    if (!newJustEatUrl.trim()) {
      setError('Please enter a JustEat URL');
      return;
    }

    setAddingSource(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/sources`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          justeat: newJustEatUrl.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add JustEat URL');
      }
      
      const data = await response.json();
      setSources(data.source_identifiers);
      setActiveSources(data.active_sources);
      setNewJustEatUrl('');
      setSuccess('JustEat URL added successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingSource(false);
    }
  };

  const handleAddDeliverooUrl = async () => {
    if (!newDeliverooUrl.trim()) {
      setError('Please enter a Deliveroo URL');
      return;
    }

    setAddingSource(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/sources`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          deliveroo: newDeliverooUrl.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add Deliveroo URL');
      }
      
      const data = await response.json();
      setSources(data.source_identifiers);
      setActiveSources(data.active_sources);
      setNewDeliverooUrl('');
      setSuccess('Deliveroo URL added successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingSource(false);
    }
  };

  const handleAddUberEatsUrl = async () => {
    if (!newUberEatsUrl.trim()) {
      setError('Please enter an UberEats URL');
      return;
    }

    setAddingSource(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/sources`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          ubereats: newUberEatsUrl.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add UberEats URL');
      }
      
      const data = await response.json();
      setSources(data.source_identifiers);
      setActiveSources(data.active_sources);
      setNewUberEatsUrl('');
      setSuccess('UberEats URL added successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingSource(false);
    }
  };

  const handleAddTripadvisorUrl = async () => {
    if (!newTripadvisorUrl.trim()) {
      setError('Please enter a Tripadvisor URL');
      return;
    }

    setAddingSource(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/sources`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          tripadvisor: newTripadvisorUrl.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add Tripadvisor URL');
      }
      
      const data = await response.json();
      setSources(data.source_identifiers);
      setActiveSources(data.active_sources);
      setNewTripadvisorUrl('');
      setSuccess('Tripadvisor URL added successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingSource(false);
    }
  };

  const handleAddBookingUrl = async () => {
    if (!newBookingUrl.trim()) {
      setError('Please enter a Booking URL');
      return;
    }

    setAddingSource(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/sources`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          booking: newBookingUrl.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add Booking URL');
      }
      
      const data = await response.json();
      setSources(data.source_identifiers);
      setActiveSources(data.active_sources);
      setNewBookingUrl('');
      setSuccess('Booking URL added successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingSource(false);
    }
  };



  const handleAddYelpUrl = async () => {
    if (!newYelpUrl.trim()) {
      setError('Please enter a Yelp URL');
      return;
    }

    setAddingSource(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/sources`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          yelp: newYelpUrl.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add Yelp URL');
      }
      
      const data = await response.json();
      setSources(data.source_identifiers);
      setActiveSources(data.active_sources);
      setNewYelpUrl('');
      setSuccess('Yelp URL added successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingSource(false);
    }
  };

  const handleAddDesignMyNightUrl = async () => {
    if (!newDesignMyNightUrl.trim()) {
      setError('Please enter a Design My Night URL');
      return;
    }

    setAddingSource(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/sources`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          designmynight: newDesignMyNightUrl.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add Design My Night URL');
      }
      
      const data = await response.json();
      setSources(data.source_identifiers);
      setActiveSources(data.active_sources);
      setNewDesignMyNightUrl('');
      setSuccess('Design My Night URL added successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingSource(false);
    }
  };

  const handleAddOpenTableUrl = async () => {
    if (!newOpenTableUrl.trim()) {
      setError('Please enter an OpenTable URL');
      return;
    }

    setAddingSource(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/sources`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          opentable: newOpenTableUrl.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add OpenTable URL');
      }
      
      const data = await response.json();
      setSources(data.source_identifiers);
      setActiveSources(data.active_sources);
      setNewOpenTableUrl('');
      setSuccess('OpenTable URL added successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingSource(false);
    }
  };

  const handleAddTrustPilotUrl = async () => {
    if (!newTrustPilotUrl.trim()) {
      setError('Please enter a TrustPilot URL');
      return;
    }

    setAddingSource(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/sources`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          trustpilot: newTrustPilotUrl.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add TrustPilot URL');
      }
      
      const data = await response.json();
      setSources(data.source_identifiers);
      setActiveSources(data.active_sources);
      setNewTrustPilotUrl('');
      setSuccess('TrustPilot URL added successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingSource(false);
    }
  };

  // Function to poll bulk fetch status
  const pollBulkFetchStatus = async (source, jobId, maxAttempts = 20) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetch(`${API_URL}/admin/businesses/${businessId}/bulk-fetch-status/${source}/${jobId}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`Status check failed: ${response.statusText}`);
        }
        
        const statusData = await response.json();
        
        if (statusData.ready_for_processing) {
          // Process the completed fetch
          const processResponse = await fetch(`${API_URL}/admin/businesses/${businessId}/process-completed-bulk-fetch/${source}/${jobId}`, {
            method: 'POST',
            credentials: 'include'
          });
          
          if (!processResponse.ok) {
            throw new Error(`Failed to process completed fetch: ${processResponse.statusText}`);
          }
          
          const processData = await processResponse.json();
          return {
            success: true,
            data: processData,
            isCompleted: true
          };
        } else {
          // Still processing, update user
          setFetchResults({
            message: `${source} reviews are being collected from the source... (${statusData.reviews_available || 0} reviews collected so far)`,
            status: 'processing',
            source: source,
            job_id: jobId,
            reviews_available: statusData.reviews_available || 0,
            attempt: attempt
          });
          
          // Wait before next poll (exponential backoff)
          const delay = Math.min(30000, 5000 * Math.pow(1.2, attempt - 1)); // Start at 5s, max 30s
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } catch (err) {
        console.error(`Polling attempt ${attempt} failed:`, err);
        if (attempt === maxAttempts) {
          throw err;
        }
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 10000));
      }
    }
    
    throw new Error('Max polling attempts reached - the bulk fetch may still be processing');
  };

  // Generic function to fetch reviews from any source
  const fetchReviewsFromSource = async (source) => {
    setFetchingReviews(true);
    setError(null);
    setFetchResults(null);
    
    try {
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/fetch-reviews/${source}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          size_limit: sizeLimit,
          monitoring_level: 'regular' // Default monitoring level for subscriptions
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to fetch ${source} reviews`);
      }
      
      const data = await response.json();
      
      // Handle different response types
      if (source === 'tripadvisor') {
        // TripAdvisor uses async processing
        setFetchResults(data);
        setSuccess(`TripAdvisor reviews task initiated! Check back in a few minutes.`);
      } else if (data.bulk_fetch && data.subscription) {
        // New Zembra bulk + subscription response
        const bulkFetch = data.bulk_fetch;
        const subscription = data.subscription;
        
        if (bulkFetch.status === 'completed') {
          // Immediate completion
          setFetchResults(data);
          const reviewCount = data.total_reviews_processed || 0;
          const subscriptionMsg = subscription.existing ? 'existing subscription' : 'new subscription';
          setSuccess(`Successfully processed ${reviewCount} ${source} reviews and set up ${subscriptionMsg}!`);
        } else if (bulkFetch.status === 'partial') {
          // Partial completion
          setFetchResults(data);
          const reviewCount = data.total_reviews_processed || 0;
          const subscriptionMsg = subscription.existing ? 'existing subscription' : 'new subscription';
          setSuccess(`Processed ${reviewCount} ${source} reviews (partial) and set up ${subscriptionMsg}. ${data.warning || ''}`);
        } else {
          // Need to poll for completion
          setFetchResults({
            message: `Setting up ${source} bulk fetch and subscription... This may take several minutes for large datasets.`,
            status: 'initializing',
            source: source,
            bulk_fetch: bulkFetch,
            subscription: subscription
          });
          
          // Start polling
          try {
            const pollResult = await pollBulkFetchStatus(source, bulkFetch.job_id);
            if (pollResult.success && pollResult.isCompleted) {
              const completedData = pollResult.data;
              setFetchResults(completedData);
              const reviewCount = completedData.reviews_processed || 0;
              const subscriptionMsg = subscription.existing ? 'existing subscription' : 'new subscription';
              setSuccess(`Successfully processed ${reviewCount} ${source} reviews and set up ${subscriptionMsg}!`);
            }
          } catch (pollError) {
            setError(`Initial setup completed but polling failed: ${pollError.message}. The subscription is active and will collect new reviews daily.`);
          }
        }
      } else {
        // Fallback for other response formats
        setFetchResults(data);
        const reviewCount = data.reviews_processed || data.reviews_fetched || 0;
        setSuccess(`Successfully processed ${reviewCount} ${source} reviews!`);
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setFetchingReviews(false);
    }
  };

  const handleFetchJustEatReviews = () => fetchReviewsFromSource('justeat');

  const handleFetchDeliverooReviews = () => fetchReviewsFromSource('deliveroo');
  const handleFetchUberEatsReviews = () => fetchReviewsFromSource('ubereats');
  const handleFetchTripadvisorReviews = () => fetchReviewsFromSource('tripadvisor');
  const handleFetchBookingReviews = () => fetchReviewsFromSource('booking');

  const handleFetchYelpReviews = () => fetchReviewsFromSource('yelp');
  const handleFetchDesignMyNightReviews = () => fetchReviewsFromSource('designmynight');
  const handleFetchOpenTableReviews = () => fetchReviewsFromSource('opentable');
  const handleFetchTrustPilotReviews = () => fetchReviewsFromSource('trustpilot');

  const handleRemoveSource = async (sourceType) => {
    setError(null);
    
    try {
      // Create a copy without the specified source
      const updatedSources = { ...sources };
      delete updatedSources[sourceType];
      
      const response = await fetch(`${API_URL}/admin/businesses/${businessId}/sources`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updatedSources)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to remove source');
      }
      
      const data = await response.json();
      setSources(data.source_identifiers);
      setActiveSources(data.active_sources);
      setSuccess(`${sourceType} source removed successfully!`);
      
    } catch (err) {
      setError(err.message);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    setFetchResults(null);
    setNewJustEatUrl('');
    setNewDeliverooUrl('');
    setNewUberEatsUrl('');
    setNewTripadvisorUrl('');
    setNewBookingUrl('');

    setNewYelpUrl('');
    setNewDesignMyNightUrl('');
    setNewOpenTableUrl('');
    setNewTrustPilotUrl('');
    setSizeLimit(5000);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Manage Sources for {businessName}
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            {/* Current Sources */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Sources
                </Typography>
                
                {Object.keys(sources).length === 0 ? (
                  <Typography color="text.secondary">
                    No additional sources configured. Google is always active.
                  </Typography>
                ) : (
                  <Box>
                    {activeSources.map((source) => (
                      <Box key={source} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={source.toUpperCase()}
                          color={source === 'google' ? 'primary' : 'secondary'}
                          icon={<CheckCircleIcon />}
                        />
                        {source !== 'google' && sources[source] && (
                          <>
                            <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
                              {sources[source]}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveSource(source)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Add JustEat URL */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add JustEat Source
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="JustEat Restaurant URL"
                    placeholder="https://www.just-eat.co.uk/restaurants-restaurant-name-location"
                    value={newJustEatUrl}
                    onChange={(e) => setNewJustEatUrl(e.target.value)}
                    disabled={addingSource}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddJustEatUrl}
                    disabled={addingSource || !newJustEatUrl.trim()}
                    startIcon={addingSource ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {addingSource ? 'Adding...' : 'Add'}
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  <strong>Format:</strong> https://www.just-eat.co.uk/restaurants-[restaurant-name]-[location]
                  <br />
                  <strong>Example:</strong> https://www.just-eat.co.uk/restaurants-mcdonalds---oxford---cornmarket-street-oxford/
                  <br />
                  Enter the full JustEat restaurant URL. This will be used to fetch reviews.
                </Typography>
              </CardContent>
            </Card>

            {/* Add Deliveroo URL */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add Deliveroo Source
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="Deliveroo Restaurant URL"
                    placeholder="https://deliveroo.co.uk/restaurants/restaurant-name/restaurant-location"
                    value={newDeliverooUrl}
                    onChange={(e) => setNewDeliverooUrl(e.target.value)}
                    disabled={addingSource}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddDeliverooUrl}
                    disabled={addingSource || !newDeliverooUrl.trim()}
                    startIcon={addingSource ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {addingSource ? 'Adding...' : 'Add'}
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  <strong>Format:</strong> https://deliveroo.co.uk/restaurants/[restaurant-name]/[restaurant-location]
                  <br />
                  <strong>Example:</strong> https://deliveroo.co.uk/restaurants/mcdonalds---oxford---cornmarket-street-oxford/
                  <br />
                  Enter the full Deliveroo restaurant URL. This will be used to fetch reviews.
                </Typography>
              </CardContent>
            </Card>

            {/* Add UberEats URL */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add UberEats Source
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="UberEats Restaurant URL"
                    placeholder="https://www.ubereats.com/gb/store/restaurant-name/restaurant-id"
                    value={newUberEatsUrl}
                    onChange={(e) => setNewUberEatsUrl(e.target.value)}
                    disabled={addingSource}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddUberEatsUrl}
                    disabled={addingSource || !newUberEatsUrl.trim()}
                    startIcon={addingSource ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {addingSource ? 'Adding...' : 'Add'}
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  <strong>Format:</strong> https://www.ubereats.com/gb/store/[restaurant-name]/[restaurant-id]
                  <br />
                  <strong>Example:</strong> https://www.ubereats.com/gb/store/mcdonalds-oxford-cornmarket/WJsdcf8_XN2KvWkfeXIG9A
                  <br />
                  Enter the full UberEats restaurant URL. This will be used to fetch reviews.
                </Typography>
              </CardContent>
            </Card>

            {/* Add Tripadvisor URL */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add Tripadvisor Source
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="Tripadvisor Restaurant URL"
                    placeholder="https://www.tripadvisor.com/Restaurant_Review-g186338-d230451-Reviews-Restaurant_Name-Restaurant_Location.html"
                    value={newTripadvisorUrl}
                    onChange={(e) => setNewTripadvisorUrl(e.target.value)}
                    disabled={addingSource}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddTripadvisorUrl}
                    disabled={addingSource || !newTripadvisorUrl.trim()}
                    startIcon={addingSource ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {addingSource ? 'Adding...' : 'Add'}
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  <strong>Format:</strong> https://www.tripadvisor.com/Restaurant_Review-g186338-d230451-Reviews-Restaurant_Name-Restaurant_Location.html
                  <br />
                  <strong>Example:</strong> https://www.tripadvisor.com/Restaurant_Review-g186338-d230451-Reviews-Restaurant_Name-Restaurant_Location.html
                  <br />
                  Enter the full Tripadvisor restaurant URL. This will be used to fetch reviews.
                </Typography>
              </CardContent>
            </Card>

            {/* Add Booking URL */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add Booking Source
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="Booking Restaurant URL"
                    placeholder="https://www.booking.com/hotel/gb/restaurant-name.html"
                    value={newBookingUrl}
                    onChange={(e) => setNewBookingUrl(e.target.value)}
                    disabled={addingSource}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddBookingUrl}
                    disabled={addingSource || !newBookingUrl.trim()}
                    startIcon={addingSource ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {addingSource ? 'Adding...' : 'Add'}
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  <strong>Format:</strong> https://www.booking.com/hotel/gb/restaurant-name.html
                  <br />
                  <strong>Example:</strong> https://www.booking.com/hotel/gb/restaurant-name.html
                  <br />
                  Enter the full Booking restaurant URL. This will be used to fetch reviews.
                </Typography>
              </CardContent>
            </Card>



            {/* Add Yelp URL */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add Yelp Source
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="Yelp Restaurant URL"
                    placeholder="https://www.yelp.com/biz/restaurant-name"
                    value={newYelpUrl}
                    onChange={(e) => setNewYelpUrl(e.target.value)}
                    disabled={addingSource}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddYelpUrl}
                    disabled={addingSource || !newYelpUrl.trim()}
                    startIcon={addingSource ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {addingSource ? 'Adding...' : 'Add'}
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  <strong>Format:</strong> https://www.yelp.com/biz/restaurant-name
                  <br />
                  <strong>Example:</strong> https://www.yelp.com/biz/mcdonalds-new-york
                  <br />
                  Enter the full Yelp restaurant URL. This will be used to fetch reviews.
                </Typography>
              </CardContent>
            </Card>

            {/* Add Design My Night URL */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add Design My Night Source
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="Design My Night Restaurant URL"
                    placeholder="https://www.designmynight.com/london/restaurants/restaurant-name"
                    value={newDesignMyNightUrl}
                    onChange={(e) => setNewDesignMyNightUrl(e.target.value)}
                    disabled={addingSource}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddDesignMyNightUrl}
                    disabled={addingSource || !newDesignMyNightUrl.trim()}
                    startIcon={addingSource ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {addingSource ? 'Adding...' : 'Add'}
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  <strong>Format:</strong> https://www.designmynight.com/london/restaurants/restaurant-name
                  <br />
                  <strong>Example:</strong> https://www.designmynight.com/london/restaurants/nobu-london
                  <br />
                  Enter the full Design My Night restaurant URL. This will be used to fetch reviews.
                </Typography>
              </CardContent>
            </Card>

            {/* Add OpenTable URL */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add OpenTable Source
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="OpenTable Restaurant URL"
                    placeholder="https://www.opentable.com/restaurant-name"
                    value={newOpenTableUrl}
                    onChange={(e) => setNewOpenTableUrl(e.target.value)}
                    disabled={addingSource}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddOpenTableUrl}
                    disabled={addingSource || !newOpenTableUrl.trim()}
                    startIcon={addingSource ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {addingSource ? 'Adding...' : 'Add'}
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  <strong>Format:</strong> https://www.opentable.com/restaurant-name
                  <br />
                  <strong>Example:</strong> https://www.opentable.com/r/nobu-london
                  <br />
                  Enter the full OpenTable restaurant URL. This will be used to fetch reviews.
                </Typography>
              </CardContent>
            </Card>

            {/* Add TrustPilot URL */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add TrustPilot Source
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="TrustPilot Business URL"
                    placeholder="https://www.trustpilot.com/review/business-domain.com"
                    value={newTrustPilotUrl}
                    onChange={(e) => setNewTrustPilotUrl(e.target.value)}
                    disabled={addingSource}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddTrustPilotUrl}
                    disabled={addingSource || !newTrustPilotUrl.trim()}
                    startIcon={addingSource ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {addingSource ? 'Adding...' : 'Add'}
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  <strong>Format:</strong> https://www.trustpilot.com/review/business-domain.com
                  <br />
                  <strong>Example:</strong> https://www.trustpilot.com/review/mcdonalds.com
                  <br />
                  Enter the full TrustPilot business URL. This will be used to fetch reviews.
                </Typography>
              </CardContent>
            </Card>

            {/* Fetch JustEat Reviews */}
            {sources.justeat && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fetch JustEat Reviews
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      type="number"
                      label="Number of Reviews to Fetch"
                      value={sizeLimit}
                      onChange={(e) => setSizeLimit(parseInt(e.target.value) || 5000)}
                      inputProps={{ min: 1, max: 5000 }}
                      helperText="Maximum 5000 reviews per fetch"
                    />
                    
                    <Button
                      variant="contained"
                      onClick={handleFetchJustEatReviews}
                      disabled={fetchingReviews}
                      startIcon={fetchingReviews ? <CircularProgress size={20} /> : <DownloadIcon />}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {fetchingReviews ? 'Fetching Reviews...' : 'Fetch Reviews'}
                    </Button>
                    
                    <Collapse in={!!fetchResults}>
                      {fetchResults && (
                        <Alert severity="info">
                          <Typography variant="body2">
                            <strong>Fetch Results:</strong><br />
                            • Reviews fetched: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_fetched || 0) : (fetchResults.reviews_fetched || 0)}<br />
                            • Reviews processed: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_processed || 0) : (fetchResults.reviews_processed || 0)}<br />
                            • Reviews skipped (duplicates): {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_skipped || 0) : (fetchResults.reviews_skipped || 0)}<br />
                            • Users needing analysis: {fetchResults.users_needing_analysis}
                          </Typography>
                        </Alert>
                      )}
                    </Collapse>
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {/* Fetch Deliveroo Reviews */}
            {sources.deliveroo && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fetch Deliveroo Reviews
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      type="number"
                      label="Number of Reviews to Fetch"
                      value={sizeLimit}
                      onChange={(e) => setSizeLimit(parseInt(e.target.value) || 5000)}
                      inputProps={{ min: 1, max: 5000 }}
                      helperText="Maximum 5000 reviews per fetch"
                    />
                    
                    <Button
                      variant="contained"
                      onClick={handleFetchDeliverooReviews}
                      disabled={fetchingReviews}
                      startIcon={fetchingReviews ? <CircularProgress size={20} /> : <DownloadIcon />}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {fetchingReviews ? 'Fetching Reviews...' : 'Fetch Reviews'}
                    </Button>
                    
                    <Collapse in={!!fetchResults}>
                      {fetchResults && (
                        <Alert severity={
                          fetchResults.status === 'processing' || fetchResults.status === 'initializing' 
                            ? 'warning' 
                            : 'info'
                        }>
                          <Typography variant="body2">
                            <strong>Fetch Results:</strong><br />
                            {fetchResults.message && (
                              <>• Status: {fetchResults.message}<br /></>
                            )}
                            {fetchResults.bulk_fetch && (
                              <>
                                • Bulk Fetch Job ID: {fetchResults.bulk_fetch.job_id}<br />
                                • Size Limit: {fetchResults.bulk_fetch.size_limit}<br />
                                • Cost: {fetchResults.bulk_fetch.cost} credits<br />
                                • Reviews Fetched: {fetchResults.bulk_fetch.reviews_fetched || 0}<br />
                                • Reviews Processed: {fetchResults.bulk_fetch.reviews_processed || 0}<br />
                                {fetchResults.bulk_fetch.reviews_skipped > 0 && (
                                  <>• Reviews Skipped (duplicates): {fetchResults.bulk_fetch.reviews_skipped}<br /></>
                                )}
                              </>
                            )}
                            {fetchResults.subscription && (
                              <>
                                • Subscription: {fetchResults.subscription.existing ? 'Already exists' : 'Newly created'}<br />
                                • Monitoring Level: {fetchResults.subscription.monitoring_level}<br />
                                {fetchResults.subscription.cost && (
                                  <>• Subscription Cost: {fetchResults.subscription.cost} credits<br /></>
                                )}
                              </>
                            )}
                            {fetchResults.reviews_available !== undefined && (
                              <>• Reviews Available: {fetchResults.reviews_available}<br /></>
                            )}
                            {fetchResults.attempt && (
                              <>• Polling Attempt: {fetchResults.attempt}<br /></>
                            )}
                            {/* Legacy format support */}
                            {!fetchResults.bulk_fetch && fetchResults.reviews_fetched !== undefined && (
                              <>• Reviews fetched: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_fetched || 0) : (fetchResults.reviews_fetched || 0)}<br /></>
                            )}
                            {!fetchResults.bulk_fetch && fetchResults.reviews_processed !== undefined && (
                              <>• Reviews processed: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_processed || 0) : (fetchResults.reviews_processed || 0)}<br /></>
                            )}
                            {!fetchResults.bulk_fetch && fetchResults.reviews_skipped !== undefined && (
                              <>• Reviews skipped (duplicates): {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_skipped || 0) : (fetchResults.reviews_skipped || 0)}<br /></>
                            )}
                            {fetchResults.users_needing_analysis !== undefined && (
                              <>• Users needing analysis: {fetchResults.users_needing_analysis}</>
                            )}
                          </Typography>
                        </Alert>
                      )}
                    </Collapse>
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {/* Fetch UberEats Reviews */}
            {sources.ubereats && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fetch UberEats Reviews
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      type="number"
                      label="Number of Reviews to Fetch"
                      value={sizeLimit}
                      onChange={(e) => setSizeLimit(parseInt(e.target.value) || 5000)}
                      inputProps={{ min: 1, max: 5000 }}
                      helperText="Maximum 5000 reviews per fetch"
                    />
                    
                    <Button
                      variant="contained"
                      onClick={handleFetchUberEatsReviews}
                      disabled={fetchingReviews}
                      startIcon={fetchingReviews ? <CircularProgress size={20} /> : <DownloadIcon />}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {fetchingReviews ? 'Fetching Reviews...' : 'Fetch Reviews'}
                    </Button>
                    
                    <Collapse in={!!fetchResults}>
                      {fetchResults && (
                        <Alert severity="info">
                          <Typography variant="body2">
                            <strong>Fetch Results:</strong><br />
                            • Reviews fetched: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_fetched || 0) : (fetchResults.reviews_fetched || 0)}<br />
                            • Reviews processed: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_processed || 0) : (fetchResults.reviews_processed || 0)}<br />
                            • Reviews skipped (duplicates): {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_skipped || 0) : (fetchResults.reviews_skipped || 0)}<br />
                            • Users needing analysis: {fetchResults.users_needing_analysis}
                          </Typography>
                        </Alert>
                      )}
                    </Collapse>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Fetch Tripadvisor Reviews */}
            {sources.tripadvisor && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fetch Tripadvisor Reviews
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      type="number"
                      label="Number of Reviews to Fetch"
                      value={sizeLimit}
                      onChange={(e) => setSizeLimit(parseInt(e.target.value) || 5000)}
                      inputProps={{ min: 1, max: 5000 }}
                      helperText="Maximum 5000 reviews per fetch"
                    />
                    
                    <Button
                      variant="contained"
                      onClick={handleFetchTripadvisorReviews}
                      disabled={fetchingReviews}
                      startIcon={fetchingReviews ? <CircularProgress size={20} /> : <DownloadIcon />}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {fetchingReviews ? 'Fetching Reviews...' : 'Fetch Reviews'}
                    </Button>
                    
                    <Collapse in={!!fetchResults}>
                      {fetchResults && (
                        <Alert severity="info">
                          <Typography variant="body2">
                            <strong>Fetch Results:</strong><br />
                            • Reviews fetched: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_fetched || 0) : (fetchResults.reviews_fetched || 0)}<br />
                            • Reviews processed: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_processed || 0) : (fetchResults.reviews_processed || 0)}<br />
                            • Reviews skipped (duplicates): {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_skipped || 0) : (fetchResults.reviews_skipped || 0)}<br />
                            • Users needing analysis: {fetchResults.users_needing_analysis}
                          </Typography>
                        </Alert>
                      )}
                    </Collapse>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Fetch Booking Reviews */}
            {sources.booking && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fetch Booking Reviews
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      type="number"
                      label="Number of Reviews to Fetch"
                      value={sizeLimit}
                      onChange={(e) => setSizeLimit(parseInt(e.target.value) || 5000)}
                      inputProps={{ min: 1, max: 5000 }}
                      helperText="Maximum 5000 reviews per fetch"
                    />
                    
                    <Button
                      variant="contained"
                      onClick={handleFetchBookingReviews}
                      disabled={fetchingReviews}
                      startIcon={fetchingReviews ? <CircularProgress size={20} /> : <DownloadIcon />}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {fetchingReviews ? 'Fetching Reviews...' : 'Fetch Reviews'}
                    </Button>
                    
                    <Collapse in={!!fetchResults}>
                      {fetchResults && (
                        <Alert severity="info">
                          <Typography variant="body2">
                            <strong>Fetch Results:</strong><br />
                            • Reviews fetched: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_fetched || 0) : (fetchResults.reviews_fetched || 0)}<br />
                            • Reviews processed: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_processed || 0) : (fetchResults.reviews_processed || 0)}<br />
                            • Reviews skipped (duplicates): {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_skipped || 0) : (fetchResults.reviews_skipped || 0)}<br />
                            • Users needing analysis: {fetchResults.users_needing_analysis}
                          </Typography>
                        </Alert>
                      )}
                    </Collapse>
                  </Box>
                </CardContent>
              </Card>
            )}



            {/* Fetch Yelp Reviews */}
            {sources.yelp && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fetch Yelp Reviews
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      type="number"
                      label="Number of Reviews to Fetch"
                      value={sizeLimit}
                      onChange={(e) => setSizeLimit(parseInt(e.target.value) || 5000)}
                      inputProps={{ min: 1, max: 5000 }}
                      helperText="Maximum 5000 reviews per fetch"
                    />
                    
                    <Button
                      variant="contained"
                      onClick={handleFetchYelpReviews}
                      disabled={fetchingReviews}
                      startIcon={fetchingReviews ? <CircularProgress size={20} /> : <DownloadIcon />}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {fetchingReviews ? 'Fetching Reviews...' : 'Fetch Reviews'}
                    </Button>
                    
                    <Collapse in={!!fetchResults}>
                      {fetchResults && (
                        <Alert severity="info">
                          <Typography variant="body2">
                            <strong>Fetch Results:</strong><br />
                            • Reviews fetched: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_fetched || 0) : (fetchResults.reviews_fetched || 0)}<br />
                            • Reviews processed: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_processed || 0) : (fetchResults.reviews_processed || 0)}<br />
                            • Reviews skipped (duplicates): {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_skipped || 0) : (fetchResults.reviews_skipped || 0)}<br />
                            • Users needing analysis: {fetchResults.users_needing_analysis}
                          </Typography>
                        </Alert>
                      )}
                    </Collapse>
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {/* Fetch Design My Night Reviews */}
            {sources.designmynight && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fetch Design My Night Reviews
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      type="number"
                      label="Number of Reviews to Fetch"
                      value={sizeLimit}
                      onChange={(e) => setSizeLimit(parseInt(e.target.value) || 5000)}
                      inputProps={{ min: 1, max: 5000 }}
                      helperText="Maximum 5000 reviews per fetch"
                    />
                    
                    <Button
                      variant="contained"
                      onClick={handleFetchDesignMyNightReviews}
                      disabled={fetchingReviews}
                      startIcon={fetchingReviews ? <CircularProgress size={20} /> : <DownloadIcon />}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {fetchingReviews ? 'Fetching Reviews...' : 'Fetch Reviews'}
                    </Button>
                    
                    <Collapse in={!!fetchResults}>
                      {fetchResults && (
                        <Alert severity="info">
                          <Typography variant="body2">
                            <strong>Fetch Results:</strong><br />
                            • Reviews fetched: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_fetched || 0) : (fetchResults.reviews_fetched || 0)}<br />
                            • Reviews processed: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_processed || 0) : (fetchResults.reviews_processed || 0)}<br />
                            • Reviews skipped (duplicates): {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_skipped || 0) : (fetchResults.reviews_skipped || 0)}<br />
                            • Users needing analysis: {fetchResults.users_needing_analysis}
                          </Typography>
                        </Alert>
                      )}
                    </Collapse>
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {/* Fetch OpenTable Reviews */}
            {sources.opentable && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fetch OpenTable Reviews
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      type="number"
                      label="Number of Reviews to Fetch"
                      value={sizeLimit}
                      onChange={(e) => setSizeLimit(parseInt(e.target.value) || 5000)}
                      inputProps={{ min: 1, max: 5000 }}
                      helperText="Maximum 5000 reviews per fetch"
                    />
                    
                    <Button
                      variant="contained"
                      onClick={handleFetchOpenTableReviews}
                      disabled={fetchingReviews}
                      startIcon={fetchingReviews ? <CircularProgress size={20} /> : <DownloadIcon />}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {fetchingReviews ? 'Fetching Reviews...' : 'Fetch Reviews'}
                    </Button>
                    
                    <Collapse in={!!fetchResults}>
                      {fetchResults && (
                        <Alert severity="info">
                          <Typography variant="body2">
                            <strong>Fetch Results:</strong><br />
                            • Reviews fetched: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_fetched || 0) : (fetchResults.reviews_fetched || 0)}<br />
                            • Reviews processed: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_processed || 0) : (fetchResults.reviews_processed || 0)}<br />
                            • Reviews skipped (duplicates): {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_skipped || 0) : (fetchResults.reviews_skipped || 0)}<br />
                            • Users needing analysis: {fetchResults.users_needing_analysis}
                          </Typography>
                        </Alert>
                      )}
                    </Collapse>
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {/* Fetch TrustPilot Reviews */}
            {sources.trustpilot && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fetch TrustPilot Reviews
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      type="number"
                      label="Number of Reviews to Fetch"
                      value={sizeLimit}
                      onChange={(e) => setSizeLimit(parseInt(e.target.value) || 5000)}
                      inputProps={{ min: 1, max: 5000 }}
                      helperText="Maximum 5000 reviews per fetch"
                    />
                    
                    <Button
                      variant="contained"
                      onClick={handleFetchTrustPilotReviews}
                      disabled={fetchingReviews}
                      startIcon={fetchingReviews ? <CircularProgress size={20} /> : <DownloadIcon />}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {fetchingReviews ? 'Fetching Reviews...' : 'Fetch Reviews'}
                    </Button>
                    
                    <Collapse in={!!fetchResults}>
                      {fetchResults && (
                        <Alert severity="info">
                          <Typography variant="body2">
                            <strong>Fetch Results:</strong><br />
                            • Reviews fetched: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_fetched || 0) : (fetchResults.reviews_fetched || 0)}<br />
                            • Reviews processed: {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_processed || 0) : (fetchResults.reviews_processed || 0)}<br />
                            • Reviews skipped (duplicates): {fetchResults.bulk_fetch ? (fetchResults.bulk_fetch.reviews_skipped || 0) : (fetchResults.reviews_skipped || 0)}<br />
                            • Users needing analysis: {fetchResults.users_needing_analysis}
                          </Typography>
                        </Alert>
                      )}
                    </Collapse>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminBusinessSourcesModal; 